import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, MessageSquareText, Sparkles, Headset,
  Plug, ShieldCheck, Tag,
} from "lucide-react";

export const CATEGORIES = [
  {
    id: "basics",
    label: "The basics",
    icon: MessageSquareText,
    items: [
      {
        q: "What is Ask Redline?",
        a: "Ask Redline is a text-message concierge for HOAs and managed communities. Residents text one dedicated number (or call it, with the AI Voice Concierge add-on) and get instant, accurate answers pulled from their own community's documents, plus help with maintenance requests, event RSVPs, dues questions and reaching a real person when they need one. It's three layers in one: an always-on AI concierge that answers automatically; an optional Community Concierge Team, where real people can step into a text conversation whenever a resident needs a human; and optional 24/7 Neighborhood Watch, our live security monitoring, where live operators can pull up cameras, assess a situation, and dispatch or escalate in real time. You choose which layers each community gets.",
      },
      {
        q: "Do residents need to download an app or create an account?",
        a: "No. It works over ordinary text messaging (SMS/MMS) and WhatsApp. If a resident can send a text, they can use it, nothing to install, no username, no password. That's the whole point: near-zero friction for every resident, including those who aren't tech-savvy.",
      },
      {
        q: "What can a resident actually do by texting?",
        a: "Ask about rules, amenities, hours, parking, pets, dues and upcoming events; report a maintenance issue and attach a photo; RSVP to a community event; flag a safety or security concern; and ask to be connected with a live person from the community team.",
      },
      {
        q: "Which channels are supported?",
        a: "SMS and MMS (so residents can send photos) plus WhatsApp today. Residents can also call the community number and talk to the concierge live by voice with the AI Voice Concierge add-on. Custom RCS branding (your verified logo and business name in the thread) is available as a request-based add-on we configure with the carrier.",
      },
      {
        q: "Can residents call and talk to it, not just text?",
        a: "Yes, with the AI Voice Concierge add-on. Residents call your community's number and the assistant answers live by phone, grounded in the same community documents, it can answer questions, log a maintenance request or take a message, and before it logs anything it reads the details back and waits for a clear 'yes'. A voicemail-and-forward safety net always sits underneath, and you can set a monthly voice-minute cap per community.",
      },
      {
        q: "Is it available around the clock?",
        a: "Yes. The AI concierge answers 24/7. Optional live human support and security monitoring run either 24/7 or on a shift schedule you define (for example, after-hours, weekends and event nights).",
      },
    ],
  },
  {
    id: "concierge",
    label: "How the AI concierge works",
    icon: Sparkles,
    items: [
      {
        q: "How does it know the answers?",
        a: "Each community uploads its own material, CC&Rs, bylaws, rules, FAQs, newsletters and announcements. The assistant answers only from that community's documents and cites the exact section it used, so replies stay grounded and verifiable rather than made up.",
      },
      {
        q: "What happens if it doesn't know something?",
        a: "It tells the resident honestly that it isn't sure instead of guessing, and quietly flags the question for your team. Every one of these 'unknowns' shows up in the dashboard, so you can see the gaps and add a document or a quick note to cover them.",
      },
      {
        q: "Does it know who it's talking to?",
        a: "Yes. Verified residents are greeted by name and unit. A new number is verified with the resident's unit number and last name plus a short confirmation code. Households are kept private, it never shares one household's information with another.",
      },
      {
        q: "Can we control its tone and personality?",
        a: "Absolutely. You set the assistant's name, tone (friendly, formal or concise), reply length, emoji style and overall warmth. You can also add blocked topics and words and a custom, polite decline message so it always sounds like your community.",
      },
      {
        q: "Will it give legal or financial advice?",
        a: "No. It's built to politely decline legal and financial questions, approvals, waivers and promises, and to route those to your team instead. Strong guardrails keep it from over-stepping.",
      },
      {
        q: "Can it answer residents in other languages?",
        a: "Yes, with the Multi-Language add-on. The assistant automatically detects and replies in each resident's language across text and voice, including Spanish, French, Portuguese, Chinese, Vietnamese and Tagalog, so every household is served in the language they're most comfortable with. You can also set a community's default language.",
      },
      {
        q: "Is each community's data kept separate?",
        a: "Yes, strict multi-tenant isolation is a core design principle. Every community's documents, roster and conversations are walled off from every other community.",
      },
    ],
  },
  {
    id: "people",
    label: "Live support & security",
    icon: Headset,
    items: [
      {
        q: "What's the difference between the concierge, live support and security monitoring?",
        a: "The AI Concierge (included in every plan) answers residents automatically. The Community Concierge Team (add-on) lets real people step into a text thread when a resident needs a human. Security Monitoring, branded Neighborhood Watch (premium add-on), adds live operators who can pull up cameras, watch a situation and dispatch or escalate in real time.",
      },
      {
        q: "What happens when a resident asks for a real person?",
        a: "If a live operator is on shift, the conversation moves to the operator desk and a person replies right in the same text thread. If no one is available, the request is logged as waiting and your team follows up, and the resident is told honestly rather than left hanging.",
      },
      {
        q: "Can our staff jump into a conversation?",
        a: "Yes. From the operator desk, staff can 'take over' a thread, which pauses the AI so there's never any double-talk, reply as a human, and then hand it back to the AI when they're done.",
      },
      {
        q: "How does security monitoring actually work?",
        a: "A resident texts in something that looks suspicious. Redline's operators can pull up the live camera in that area, assess what's happening, and respond, dispatch or escalate in real time. Tiers range from log-and-queue only, to live operators during covered hours, to live operators 24/7 with optional on-call escalation.",
      },
      {
        q: "What about real emergencies?",
        a: "For anything life-threatening, the assistant always tells the resident to call 911 immediately and logs the incident for the community team. This safety guardrail is always on and can never be turned off.",
      },
    ],
  },
  {
    id: "setup",
    label: "Setup, data & integrations",
    icon: Plug,
    items: [
      {
        q: "How long does it take to get set up?",
        a: "Usually quick. We provision your dedicated text number, load your documents and resident roster, set your tone and coverage hours, and you're live. Most of the heavy lifting happens on our side.",
      },
      {
        q: "How do residents get the number?",
        a: "You can invite residents by text or email straight from the dashboard, including a compliant opt-in message, one household at a time or everyone at once. Residents can also simply text the number and self-register on the spot.",
      },
      {
        q: "Does it integrate with our management portal?",
        a: "Portal integrations are available as an add-on for TownSq, Vantaca and AppFolio, with more platforms on the roadmap. If your system offers an API, we can usually connect it, just tell us what you use.",
      },
      {
        q: "Can we import our resident roster?",
        a: "Yes. Import a CSV of your households or add them manually, then invite everyone with a single click.",
      },
      {
        q: "Who manages the documents and announcements?",
        a: "You do, from the dashboard. Upload PDFs or paste text, or post a quick note or announcement. The assistant re-indexes the new material automatically so answers stay current.",
      },
    ],
  },
  {
    id: "privacy",
    label: "Privacy, compliance & safety",
    icon: ShieldCheck,
    items: [
      {
        q: "How do residents opt in to receive texts?",
        a: "Three simple ways, all with clear consent. (1) A per-community opt-in page (a link or QR code you can post at the mailroom, clubhouse or gate) where a resident enters their number and agrees. (2) Texting the community's dedicated number, they get a one-time confirmation with how to opt out. (3) Your HOA roster, where documented consent is captured and dated for each household. Every opt-in, no matter the source, is stored as a timestamped, exportable consent record for your audit trail.",
      },
      {
        q: "Why does opt-in matter, and how are you compliant?",
        a: "Sending community texts is governed by the TCPA (federal law) and the CTIA's messaging guidelines, and enforced by carriers through A2P 10DLC registration. In plain terms: residents must knowingly agree before you text them, they must be told what kind of messages they'll get and how often, and they must always be able to stop. Getting this right protects your community from complaints, carrier filtering and legal exposure. Ask Redline is built for it end-to-end, clear, separate consent language at every opt-in point; the required 'message frequency varies' and 'message & data rates may apply' disclosures; automatic STOP/HELP/START handling honored immediately; a privacy policy stating we never sell or share mobile numbers for marketing; and a complete, exportable log of who consented, when and how.",
      },
      {
        q: "Is it text-messaging (TCPA) compliant?",
        a: "Yes. Residents receive a clear disclosure and opt-in, and the standard STOP, HELP and START keywords are always honored. Opt-outs take effect immediately.",
      },
      {
        q: "What data do you store, and who can see it?",
        a: "Conversation history and requests are stored for the community's records and support, isolated per community. Only authorized staff for that community can view them, based on their role, super admin, community manager, board member (read-only) or operator.",
      },
      {
        q: "Can residents opt out?",
        a: "Anytime, by replying STOP. They'll stop receiving messages immediately and can opt back in later by replying START.",
      },
      {
        q: "How do you prevent the bot from being manipulated?",
        a: "Built-in guardrails detect and refuse prompt-injection and off-topic or adversarial requests, rate-limit spam, and keep answers grounded in your documents. Sensitive situations are escalated to a human.",
      },
    ],
  },
  {
    id: "plans",
    label: "Plans & rollout",
    icon: Tag,
    items: [
      {
        q: "How do plans work?",
        a: "One plan per community, sized to the community, and every plan includes the AI concierge plus the command center behind it: requests, broadcasts, documents, events, contacts and reporting. Add-on modules layer on top when you want them: AI Voice Concierge (live phone answering), Field Services (vendors, work orders and invoices), WhatsApp, Multi-Language, Portfolio Intelligence, a live Community Concierge Team, and Neighborhood Watch security monitoring.",
      },
      {
        q: "Do you have a plan for HOA management companies?",
        a: "Yes, there's an Enterprise plan built for management companies running a portfolio: unlimited communities and phone numbers, a portfolio dashboard with team roles, all portal integrations (TownSq, Vantaca, AppFolio), and dedicated onboarding with an SLA.",
      },
      {
        q: "Is the concierge included in every plan?",
        a: "Yes, the automated concierge is standard on every plan. The Community Concierge Team and Neighborhood Watch (security monitoring) simply layer on top when you want live people involved.",
      },
      {
        q: "How do we get a quote or see a demo?",
        a: "Book a demo below. We'll walk you through it, look at your community's size and coverage needs, and put a proposal together for you.",
      },
    ],
  },
];

function FaqItem({ item, isOpen, onToggle, testid }) {
  return (
    <div className="bg-white rounded-2xl border border-sand-200 overflow-hidden">
      <button
        data-testid={testid}
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between gap-4 text-left px-5 py-4 md:px-6 md:py-5 hover:bg-sand-50 transition-colors"
      >
        <span className="font-heading font-bold text-ink text-base md:text-lg">{item.q}</span>
        <ChevronDown
          className={`h-5 w-5 shrink-0 text-redline-500 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <p className="px-5 pb-5 md:px-6 md:pb-6 text-ink-700 text-sm md:text-base leading-relaxed">
              {item.a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQ() {
  const [open, setOpen] = useState("basics-0");

  return (
    <section id="faq" className="max-w-4xl mx-auto px-6 py-24">
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Questions, answered</p>
        <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Everything about Ask Redline</h2>
        <p className="text-ink-700 mt-4 leading-relaxed">
          How the concierge works, what residents can do, what's automated versus handled by a real person,
          and how setup, privacy and rollout work. Still curious? <a href="#contact" className="text-redline-600 font-semibold hover:underline">Ask us directly.</a>
        </p>
      </div>

      <div className="mt-12 space-y-12">
        {CATEGORIES.map((cat) => (
          <div key={cat.id} data-testid={`faq-category-${cat.id}`}>
            <div className="flex items-center gap-2.5 mb-5">
              <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0">
                <cat.icon className="h-4 w-4 text-redline-600" strokeWidth={1.75} />
              </div>
              <h3 className="font-heading text-lg font-extrabold tracking-tight">{cat.label}</h3>
            </div>
            <div className="space-y-3">
              {cat.items.map((item, i) => {
                const key = `${cat.id}-${i}`;
                return (
                  <FaqItem
                    key={key}
                    item={item}
                    isOpen={open === key}
                    onToggle={() => setOpen(open === key ? null : key)}
                    testid={`faq-q-${cat.id}-${i}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 rounded-3xl bg-ink text-white p-8 md:p-10 text-center">
        <h3 className="font-heading text-2xl font-extrabold tracking-tight">Didn't find your answer?</h3>
        <p className="text-sand-200 mt-2 max-w-lg mx-auto">Tell us about your community and we'll show you exactly how Ask Redline would work for your residents.</p>
        <a href="#contact" data-testid="faq-book-demo-btn"
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 hover:bg-redline-600 hover:-translate-y-0.5 transition-all shadow-lg shadow-redline-500/20">
          Book a demo
        </a>
      </div>
    </section>
  );
}
